import { getCtx } from "./sound";
import { clipBuffer } from "./audioStore";

let reverbBuf = null;

const impulse = (c, seconds = 3.4, decay = 2.4) => {
  const len = Math.floor(c.sampleRate * seconds);
  const buf = c.createBuffer(2, len, c.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
  }
  return buf;
};

const fetchBuffer = async (c, url) => {
  const buf = await clipBuffer(url);
  if (!buf) throw new Error("no clip");
  return c.decodeAudioData(buf);
};

const whoosh = (c, out, t, dur = 1.6) => {
  const len = Math.floor(c.sampleRate * dur);
  const buf = c.createBuffer(1, len, c.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  const src = c.createBufferSource();
  src.buffer = buf;
  const bp = c.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.2;
  bp.frequency.setValueAtTime(180, t);
  bp.frequency.exponentialRampToValueAtTime(2600, t + dur * 0.7);
  bp.frequency.exponentialRampToValueAtTime(400, t + dur);
  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.exponentialRampToValueAtTime(0.22, t + dur * 0.45);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  src.connect(bp).connect(g).connect(out);
  src.start(t);
  src.stop(t + dur + 0.05);
  return src;
};

const pad = (c, out, t, dur) => {
  const nodes = [];
  [220, 329.6, 554.4, 440].forEach((f, i) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = f * (1 + (i % 2 ? 0.003 : -0.003));
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.028, t + 2.5);
    g.gain.setValueAtTime(0.028, t + Math.max(3, dur - 2.5));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 1);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + dur + 1.1);
    nodes.push(o);
  });
  return nodes;
};

// Pensieve playback: whoosh -> looping sound bed (low-passed, reverb) + chord pad -> Monroe's voice with reverb tail.
export const playMemory = async ({ voiceUrl, sfxUrl, onProgress, onEnd }) => {
  const c = getCtx();
  reverbBuf = reverbBuf || impulse(c);
  const master = c.createGain();
  master.gain.value = 1;
  master.connect(c.destination);
  const verb = c.createConvolver();
  verb.buffer = reverbBuf;
  const verbGain = c.createGain();
  verbGain.gain.value = 0.4;
  verb.connect(verbGain).connect(master);

  const [voice, sfx] = await Promise.all([
    voiceUrl ? fetchBuffer(c, voiceUrl).catch(() => null) : null,
    sfxUrl ? fetchBuffer(c, sfxUrl).catch(() => null) : null,
  ]);

  const LEAD = 1.4;
  const voiceDur = voice ? voice.duration : 0;
  const total = LEAD + Math.max(voiceDur, 4) + 2.5;
  const t0 = c.currentTime + 0.05;
  const nodes = [whoosh(c, verb, t0), ...pad(c, verb, t0, total)];
  let stopped = false;
  let raf = 0;

  if (sfx) {
    const s = c.createBufferSource();
    s.buffer = sfx;
    s.loop = true;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 2400;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.55, t0 + 2);
    g.gain.setValueAtTime(0.55, t0 + total - 1.5);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + total + 1.2);
    s.connect(lp).connect(g);
    g.connect(master);
    g.connect(verb);
    s.start(t0);
    s.stop(t0 + total + 1.3);
    nodes.push(s);
  }
  if (voice) {
    const v = c.createBufferSource();
    v.buffer = voice;
    const lp = c.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 5600;
    const g = c.createGain();
    g.gain.value = 1.05;
    const send = c.createGain();
    send.gain.value = 0.7;
    v.connect(lp).connect(g);
    g.connect(master);
    g.connect(send).connect(verb);
    v.start(t0 + LEAD);
    nodes.push(v);
  }

  const tick = () => {
    if (stopped) return;
    const el = c.currentTime - t0;
    onProgress?.(Math.max(0, el - LEAD), voiceDur);
    if (el < total + 1.2) raf = requestAnimationFrame(tick);
    else { stopped = true; onEnd?.(); }
  };
  raf = requestAnimationFrame(tick);

  return {
    hasVoice: Boolean(voice),
    voiceDuration: voiceDur,
    stop: () => {
      if (stopped) return;
      stopped = true;
      cancelAnimationFrame(raf);
      master.gain.setValueAtTime(master.gain.value, c.currentTime);
      master.gain.linearRampToValueAtTime(0.0001, c.currentTime + 0.35);
      setTimeout(() => nodes.forEach((n) => { try { n.stop(); } catch (e) { /* already stopped */ } }), 380);
    },
  };
};

export const splitSentences = (text = "") => (text.match(/[^.!?]+[.!?]+["']?|[^.!?]+$/g) || [text]).map((s) => s.trim()).filter(Boolean);
